import { api, ep, getCurrentUser } from "aldoo-ra/API"

// Endpoints. Credentials are injected automatically by api() from the
// platform user-credentials store, so no auth argument is threaded here.
const orderAPI = api("order")
const shareAPI = api("share")
const userAPI = api("user")
const themeAPI = api("share-theme")

// ── Account / profile ────────────────────────────────────────────────────────
export const ChangeUserName = (name) =>
  userAPI({ profile: { update: { name: name.trim() } } })

// ── Orders ───────────────────────────────────────────────────────────────────
export const CreateOrder = (type) => orderAPI({ type })

export const GetOrderStatus = (orderID, listFiles, expose) =>
  orderAPI({ status: { orderID, listFiles, expose } })

// Plan transfer limits + rolling 30-day usage.
export const GetQuota = () => orderAPI({ quota: {} })

// ── Uploads (multipart) ──────────────────────────────────────────────────────
export const UploadShareFile = (orderID, file, onProgress, signal, relPath) => {
  const formData = new FormData()
  formData.append("addShareFile", "1")
  formData.append("orderID", orderID)
  formData.append("document", file)
  if (relPath) formData.append("relPath", relPath)
  return orderAPI(formData, {
    signal,
    onUploadProgress: onProgress ? (e) => onProgress(e.loaded, e.total || 0) : undefined,
  })
}

export const UploadShareChunk = (
  orderID, chunkIndex, totalChunks, fileName, fileSize, chunkBlob, onProgress, signal
) => {
  const formData = new FormData()
  formData.append("addShareChunk", "1")
  formData.append("orderID", orderID)
  formData.append("chunkIndex", chunkIndex)
  formData.append("totalChunks", totalChunks)
  formData.append("fileName", fileName)
  formData.append("fileSize", fileSize)
  formData.append("chunk", chunkBlob, fileName)
  return orderAPI(formData, {
    signal,
    onUploadProgress: onProgress ? (e) => onProgress(e.loaded, e.total || 0) : undefined,
  })
}

export const FinalizeShareUpload = (orderID) =>
  orderAPI({ finalizeShareUpload: { orderID } })

// ── File share lifecycle (action-based) ──────────────────────────────────────
export const ProcessFileShare = (orderID, { retentionDays, accessType, access, notes, password, title, downloadLimit, themeId }) =>
  orderAPI({ fileShare: { action: "create", orderID, retentionDays, accessType, access, notes, password, title, downloadLimit, themeId } })

export const DeleteFileShare = (orderID) =>
  orderAPI({ fileShare: { action: "delete", orderID } })

// Commit files just uploaded (via UploadShareFile / FinalizeShareUpload) into
// an already-created share. Server moves tmp → permanent path and updates the
// manifest so future downloaders see the new files.
export const AppendShareFiles = (orderID) =>
  orderAPI({ fileShare: { action: "addFiles", orderID } })

// ── File share management (dashboard) ────────────────────────────────────────
export const ListFileShares = ({ page = 1, itemsPerPage = 10, search, statusFilter } = {}) =>
  orderAPI({ fileShare: { action: "list", page, itemsPerPage, search, statusFilter } })

// Pro-only: extend (recover) a share with a fresh allocation of N days.
export const ExtendFileShare = (orderID, retentionDays) =>
  orderAPI({ fileShare: { action: "extend", orderID, retentionDays } })

export const DeleteManyFileShares = (orderIDs) =>
  orderAPI({ fileShare: { action: "deleteMany", orderIDs } })

export const PauseFileShare = (orderID) =>
  orderAPI({ fileShare: { action: "pause", orderID } })

export const ResumeFileShare = (orderID) =>
  orderAPI({ fileShare: { action: "resume", orderID } })

export const SetShareAccessType = (orderID, accessType) =>
  orderAPI({ fileShare: { action: "setAccessType", orderID, accessType } })

export const AddShareAccess = (orderID, email) =>
  orderAPI({ fileShare: { action: "addAccess", orderID, email } })

export const RemoveShareAccess = (orderID, email) =>
  orderAPI({ fileShare: { action: "removeAccess", orderID, email } })

export const SetDownloadLimit = (orderID, limit) =>
  orderAPI({ fileShare: { action: "setDownloadLimit", orderID, limit: limit ?? null } })

export const RemoveShareFile = (orderID, fileIndex) =>
  orderAPI({ fileShare: { action: "removeFile", orderID, fileIndex } })

// Email the share link to recipients (plan-capped server-side).
export const SendShareLink = (orderID, emails, message) =>
  orderAPI({ fileShare: { action: "send", orderID, emails, message } })

export const SetShareNotes = (orderID, notes) =>
  orderAPI({ fileShare: { action: "setNotes", orderID, notes } })

// Rename a share after the fact.
export const SetShareTitle = (orderID, title) =>
  orderAPI({ fileShare: { action: "setTitle", orderID, title } })

export const SetSharePassword = (orderID, clientHash) =>
  orderAPI({ fileShare: { action: "setPassword", orderID, password: clientHash } })

export const RemoveSharePassword = (orderID) =>
  orderAPI({ fileShare: { action: "removePassword", orderID } })

// Restamp the theme on an existing share. `themeId` is either an owned
// theme's _id, the virtual DEFAULT_THEME_ID (server falls back to the user's
// default), or null/undefined.
export const SetShareTheme = (orderID, themeId) =>
  orderAPI({ fileShare: { action: "setTheme", orderID, themeId } })

// ── Public share access ──────────────────────────────────────────────────────
export const GetShareConfig = () => shareAPI({ share: { action: "config" } })

export const GetShareInfo = (token, password) =>
  shareAPI({ share: { action: "info", token, ...(password ? { password } : {}) } })

export const VerifySharePassword = (token, password) =>
  shareAPI({ share: { action: "verify-password", token, password } })

export const GetShareDownloadUrl = (token, password, fileIndex) =>
  shareAPI({ share: { action: "download", token, ...(fileIndex != null ? { fileIndex } : {}), ...(password ? { password } : {}) } })

// Server-generated thumbnail for an image file in a share (same one the mobile
// clients use). Lazy-generated server-side and returned as a signed JPEG URL —
// any image source (incl. heic/tiff) comes back browser-renderable. Returns
// { url } on success, or { error: "no_thumb" | ... } for non-images / failures.
export const GetShareThumb = (token, fileIndex, password) =>
  shareAPI({ share: { action: "thumb", token, ...(fileIndex != null ? { fileIndex } : {}), ...(password ? { password } : {}) } })

// Signed URL to the full original file for inline preview (image/video overlay).
// preview:true so the server does NOT count it as a download or fire limit/
// first-download notifications. The URL carries an attachment disposition, which
// browsers ignore for <img>/<video> element loads — it renders inline.
export const GetSharePreviewUrl = (token, fileIndex, password) =>
  shareAPI({ share: { action: "download", token, preview: true, ...(fileIndex != null ? { fileIndex } : {}), ...(password ? { password } : {}) } })

// POST + save: streams the zip from the server and saves it via a Blob URL.
// POST so the Authorization header rides through (browsers can't attach
// custom headers to top-level GET navs). The whole zip is buffered in the
// browser before it lands on disk, which is fine for typical share sizes
// but not ideal for multi-GB transfers — switch to a Service Worker
// streaming download if that becomes a real constraint.
async function streamingDownload(endpoint, body) {
  // Raw fetch — keeps the streaming response body and lets us read the
  // Content-Disposition header to derive the filename. AldooAPI's post()
  // wrapper would parse the body as JSON and clobber the stream.
  const headers = { "Content-Type": "application/json" }
  const token = getCurrentUser?.()?.auth_token
  if (token) headers.Authorization = token
  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(body || {}),
  })
  if (!res.ok) {
    const err = await res.text().catch(() => res.statusText)
    throw new Error(err || `download_failed_${res.status}`)
  }
  const disposition = res.headers.get("Content-Disposition") || ""
  let filename = "download.zip"
  const m = disposition.match(/filename="?([^"]+)"?/i)
  if (m) { try { filename = decodeURIComponent(m[1]) } catch { filename = m[1] } }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// Prepare a one-time URL for the browser to GET directly. The browser streams
// the zip to disk natively — download begins instantly, no in-memory blob.
export const PrepareDownloadShareAll = (token, password) =>
  shareAPI({ share: { action: "download-all-prepare", token, ...(password ? { password } : {}) } })

export const PrepareDownloadShareFolder = (token, password, folderPath) =>
  shareAPI({ share: { action: "download-folder-prepare", token, path: folderPath, ...(password ? { password } : {}) } })

// Legacy POST + blob-buffered download. Kept for any caller that hasn't
// migrated; new UI should use Prepare* + window.location.href.
export const DownloadShareAll = (token, password) =>
  streamingDownload(ep(`share/download-all/${encodeURIComponent(token)}`), password ? { password } : {})

export const DownloadShareFolder = (token, password, folderPath) =>
  streamingDownload(
    ep(`share/download-folder/${encodeURIComponent(token)}`),
    password ? { path: folderPath, password } : { path: folderPath },
  )

// ── User share themes ─────────────────────────────────────────────────────────
// All owner-scoped server-side; create/update/clone are Pro-gated and return
// { error: "feature_locked" } when the plan doesn't include custom themes.
// ListThemes also returns { canCustomize } to drive the GoPremium gate.
export const ListThemes = () => themeAPI({ list: {} })

// theme: { _id?, name, background_image, color_background, color_text, color_primary }
export const SaveTheme = (theme) => themeAPI({ save: { theme } })

// Clone an owned theme (themeId) or a raw payload (theme) — the latter clones
// the built-in default. Copy is owned by the caller and never the default.
export const CloneTheme = ({ themeId, theme } = {}) => themeAPI({ clone: { themeId, theme } })

export const DeleteTheme = (themeId) => themeAPI({ delete: { themeId } })

export const SetDefaultTheme = (themeId) => themeAPI({ setDefault: { themeId } })
